import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { fetchCrops, fetchDiseases, fetchSymptomsByLevel, fetchSymptomsByDisease } from '../api/cropService';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { getCurrentLanguage } from '../api/languageHelper';
import { getaccesstoken } from '../context/AuthContext';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const EditProblem = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [diseases, setDiseases] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [l1Symptoms, setL1Symptoms] = useState([]);
  const [l2Symptoms, setL2Symptoms] = useState([]);
  const [l2ToL1Map, setL2ToL1Map] = useState({});
  const [symptoms, setSymptoms] = useState([]);
  const [deletedSymptoms, setDeletedSymptoms] = useState([]);
  const [newSymptoms, setNewSymptoms] = useState([]);
  const [allL2L3Symptoms, setAllL2L3Symptoms] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    scientificName: '',
    description: '',
    chemicalAdvice: '',
    organiceAdvice: '',
    ipmMeasures: '',
  });

  useEffect(() => {
    const loadInitialData = async () => {
      const cropsData = await fetchCrops(true);
      setCrops(cropsData);
      if (cropsData.length > 0) {
        const firstCrop = cropsData[0];
        setSelectedCrop(firstCrop);
        const diseasesData = await fetchDiseases(firstCrop.cid, true);
        setDiseases(diseasesData);
        if (diseasesData.length > 0) {
          const firstDisease = diseasesData[0];
          selectDisease(firstDisease);
        }
      }
    };

    loadInitialData();
  }, []);

  const selectDisease = async (disease) => {
    setSelectedDisease([]);
    setSelectedDisease(disease);
    setL1Symptoms([]);
    setL2Symptoms([]);
    setL2ToL1Map([]);
    setSymptoms([]);
    setFormData({
      name: disease.chemical_name || '',
      scientificName: disease.scientific_name || '',
      description: disease.description || '',
      chemicalAdvice: disease.chemicalAdvice || '',
      organiceAdvice: disease.organiceAdvice || '',
    });

    try {
      const symptomData = await fetchSymptomsByDisease(disease.disId, true);
      setSymptoms(symptomData);
    } catch (err) {
      setSymptoms([]);
    }
    const l1Data = await fetchSymptomsByLevel(disease.cid, 1, true);
    setL1Symptoms(l1Data);
    const l2Data = await fetchSymptomsByLevel(disease.cid, 2, true);
    setL2Symptoms(l2Data);
    // Fetch all L2 + L3 symptoms for that crop
    const l3Data = await fetchSymptomsByLevel(disease.cid, 3, true);
    setAllL2L3Symptoms([...l2Data, ...l3Data]);
    // build map here and store in state
    const map = {};
    l2Data.forEach(l2 => {
      map[l2.sid] = l2.parentId;
    });
    setL2ToL1Map(map);
  };

  const handleSymptomChange = (index, e) => {
    const updated = [...symptoms];
    const { name, value } = e.target;
    const symptom = { ...updated[index] };

    if (name === "level1SymptomId") {
      if (symptom.level === 2) {
        symptom.parentId = parseInt(value) || null;
      }
    } else if (name === "level2SymptomId") {
      // For L3 symptoms, L2 is their direct parent
      if (symptom.level === 3) {
        symptom.parentId = parseInt(value) || null;
      }
    } else {
      symptom[name] = value;
    }

    updated[index] = symptom;
    setSymptoms(updated);
  };

  const removeSymptom = (index) => {
    const symptomToDelete = symptoms[index];

    if (symptomToDelete.symptomId != '') {
      // Add to deleted list
      setDeletedSymptoms((prev) => [...prev, symptomToDelete]);
    }

    // Remove from local display
    const updated = [...symptoms];
    updated.splice(index, 1);
    setSymptoms(updated);
  };

  const handleNewSymptomChange = (index, e) => {
    const updated = [...newSymptoms];
    updated[index][e.target.name] = e.target.value;
    setNewSymptoms(updated);
  };

  const handleExistingSymptomSelect = (index, value) => {
    const updated = [...newSymptoms];
    updated[index].existingSymptomId = value;

    if (value) {
      const selected = allL2L3Symptoms.find(s => s.sid === parseInt(value));
      if (selected) {
        updated[index].name = selected.name;
        updated[index].qn = selected.qn || '';
        updated[index].level = selected.level;
        updated[index].parentId = selected.level == 2 ? selected.parentId : l2ToL1Map[selected.parentId];
      }
    } else {
      updated[index] = {
        ...updated[index],
        existingSymptomId: '',
        name: '',
        qn: '',
        level: 2,
        parentId: null,
      };
    }

    setNewSymptoms(updated);
  };

  const removeNewSymptom = (index) => {
    const updated = [...newSymptoms];
    updated.splice(index, 1);
    setNewSymptoms(updated);
  };


  const handleDiseaseFieldChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const refreshData = async (crop, disease) => {
    if (crop && disease) {
      try {
        // Re-fetch the updated disease info
        const updatedDiseases = await fetchDiseases(crop.cid, true);
        setDiseases(updatedDiseases);
        const updatedDisease = updatedDiseases.find(
          d => d.disId === disease.disId
        );
        if (updatedDisease) {
          await selectDisease(updatedDisease);
        } else {
          console.warn("Updated disease not found after refresh");
        }
      } catch (err) {
        console.error("Error refreshing disease after update:", err);
      }
    }
  };


  const handleSubmit = async () => {
    if (!selectedDisease || !selectedCrop) return;
    const headers = {
      'auth-token': getaccesstoken(),
    };

    try {
      const disid = selectedDisease.disId;

      // 1 Delete all marked symptoms first
      for (const symptom of deletedSymptoms) {
        const sid = symptom.sid;
        const level = symptom.level;
        await axiosInstance.delete(`${apiPrefix}disease/${disid}/symptoms/${sid}/${level}`, { headers });
      }

      // 2️ Update the disease locale
      const locale = getCurrentLanguage();
      const payload = {
        cid: selectedCrop.cid,
        chemicalName: formData.name,
        scientificName: formData.scientificName,
        chemicalAdvice: formData.chemicalAdvice,
        organicAdvice: formData.organiceAdvice,
        ipmMeasures: formData.ipmMeasures || '',
        locale_code: locale,
        description: formData.description,
      };
      const response = await axiosInstance.put(`${apiPrefix}disease/${disid}`, payload, { headers });

      // 3 Update symptoms
      for (const symptom of symptoms) {
        const sid = symptom.sid;
        let local_sid = 0;
        const level = symptom.level;
        if (level == 1) {
          local_sid = sid + 100 - (256 * selectedCrop.cid);
        }
        else if (level == 2) {
          local_sid = sid + 300 - (256 * selectedCrop.cid);
        }
        else if (level == 3) {
          local_sid = sid + 600 - (256 * selectedCrop.cid);
        }
        const name = symptom.name;
        const question = symptom.qn || '';
        const parent_id = symptom.parentId || -1;
        let grandparent_id = null;

        if (level === 3 && l2ToL1Map[symptom.parentId]) {
          grandparent_id = l2ToL1Map[symptom.parentId];
        }

        const symptomData = {
          parent_id,
          grandparent_id,
          level,
          locale_code: locale,
          name,
          description: symptom.name || '',
          question,
        };

        await axiosInstance.put(`${apiPrefix}symptom/${selectedCrop.cid}/${local_sid}`, symptomData, { headers });
      }


      // 4️ Clear deleted symptoms from local state
      setDeletedSymptoms([]);

      // 5️ Add new symptoms
      const existingSymptomAdds = newSymptoms.filter(s => s.existingSymptomId);
      const newSymptomAdds = newSymptoms.filter(s => !s.existingSymptomId);
      if (newSymptomAdds.length > 0) {
        const formattedSymptoms = newSymptomAdds.map(sym => {
          const base = {
            cid: selectedCrop.cid,
            sid: undefined,
            locale_code: locale,
            name: sym.name,
            description: sym.description || sym.name,
            qn: sym.qn,
            level: parseInt(sym.level),
          };
          if (sym.level == 2) {
            base.parent_id = parseInt(sym.parentId);
          } else if (sym.level == 3) {
            base.parent_id = parseInt(sym.parentId);
            base.grandparent_id = l2ToL1Map[sym.parentId] || null;
          }

          return base;
        });
        const symptomResponse = await axiosInstance.post(`${apiPrefix}symptom/array/post`, formattedSymptoms, { headers });
        // Normalize backend shape: could be { data: [...] } or [...]
        const createdList = Array.isArray(symptomResponse.data?.data)
          ? symptomResponse.data.data
          : Array.isArray(symptomResponse.data)
            ? symptomResponse.data
            : (symptomResponse.data?.result || []);
        // Now map to problem_symptom table
        const mapPayload = createdList.map((s) => ({
          disid: selectedDisease.disId,
          cid: selectedCrop.cid,
          sid: s.id, // comes from server response
          level: formattedSymptoms.find(fs => fs.name === s.name).level
        }));
        const mapResponse = await axiosInstance.post(`${apiPrefix}disease/array/post`, mapPayload, { headers });
      }

      // 6️ Directly map existing symptoms to problem_symptom
      if (existingSymptomAdds.length > 0) {
        const existingMapPayload = existingSymptomAdds.map(s => ({
          disid: selectedDisease.disId,
          cid: selectedCrop.cid,
          sid: parseInt(s.existingSymptomId),
          level: parseInt(s.level),
        }));

        await axiosInstance.post(`${apiPrefix}disease/array/post`, existingMapPayload, { headers });
      }
      setNewSymptoms([]);
      alert(t('expert.editProblem.successMessage'));
    } catch (err) {
      console.error('Error while submitting update:', err.response?.data || err.message);
      alert(t('expert.editProblem.failMessage'));
    } finally {
      await refreshData(selectedCrop, selectedDisease);
    }
  };


  return (
    <div className="flex p-6 space-x-6">

      {/* RIGHT: Edit Form */}
      <div className="w-2/3 p-4 bg-white rounded shadow">
        {selectedDisease && (
          <>
            <h2 className="text-xl font-semibold mb-3">{t('expert.editProblem.pageTitle')} {selectedDisease.chemical_name || selectedDisease.scientific_name}</h2>
            <div className="space-y-3">
              <label className="block">
                {t('expert.editProblem.cropLabel')} <strong>{selectedCrop?.name}</strong>
              </label>
              <label className="block">
                {t('expert.editProblem.problemCNameLabel')}
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

              <label className="block">
                {t('expert.editProblem.problemSNameLabel')}
                <input
                  name="scientificName"
                  value={formData.scientificName}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

              <label className="block">
                {t('expert.editProblem.problemDescriptionLabel')}
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

              <label className="block">
                {t('expert.editProblem.chemicalAdviceLabel')}
                <textarea
                  name="chemicalAdvice"
                  value={formData.chemicalAdvice}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

              <label className="block">
                {t('expert.editProblem.organicAdviceLabel')}
                <textarea
                  name="organiceAdvice"
                  value={formData.organiceAdvice}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

              <label className="block">
                {t('expert.editProblem.ipmMeasuresLabel')}
                <textarea
                  name="ipmMeasures"
                  value={formData.ipmMeasures}
                  onChange={handleDiseaseFieldChange}
                  className="w-full border p-2 rounded"
                />
              </label>

            </div>

            <h3 className="mt-6 text-lg font-semibold">{t('expert.editProblem.symptomsSectionTitle')}</h3>
            {symptoms.map((symptom, index) => (
              <div key={symptom.sid || index} className="border p-3 mt-2 rounded space-y-2 bg-gray-50">
                <div>
                  <label>{t('expert.editProblem.symptomNameLabel')}</label>
                  <input
                    name="name"
                    value={symptom.name}
                    onChange={(e) => handleSymptomChange(index, e)}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label>{t('expert.editProblem.questionLabel')}</label>
                  <input
                    name="qn"
                    value={symptom.qn}
                    onChange={(e) => handleSymptomChange(index, e)}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label>{t('expert.editProblem.l1SymptomLabel')}</label>
                  <select
                    name="level1SymptomId"
                    value={
                      symptom.level === 2
                        ? symptom.parentId || ''
                        : symptom.level === 3
                          ? (l2ToL1Map[symptom.parentId] || '')
                          : ''
                    }
                    onChange={(e) => handleSymptomChange(index, e)}
                    disabled={symptom.level === 3} // disable dropdown for level 3
                    className="w-full border p-2 rounded"
                  >
                    <option value="">{t('expert.editProblem.noneOption')}</option>
                    {l1Symptoms.map((l1) => (
                      <option key={l1.sid} value={l1.sid}>
                        {l1.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* L2 symptom dropdown only for L3 symptoms */}
{symptom.level === 3 && (
  <div>
    <label>L2 Symptom</label>
    <select
      name="level2SymptomId"
      value={symptom.parentId || ''}
      onChange={(e) => handleSymptomChange(index, e)}
      className="w-full border p-2 rounded"
    >
      <option value="">{t('expert.editProblem.noneOption')}</option>
      {l2Symptoms.map((l2) => (
        <option key={l2.sid} value={l2.sid}>
          {l2.name}
        </option>
      ))}
    </select>
  </div>
)}
                <button
                  type="button"
                  onClick={() => removeSymptom(index)}
                  className="text-red-500 mt-2"
                >
                  {t('expert.editProblem.deleteSymptomButton')}
                </button>
              </div>
            ))}

            {newSymptoms.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold">
                  {t('expert.editProblem.newSymptomsTitle')}
                </h3>
                {newSymptoms.map((symptom, index) => (
                  <div key={index} className="border p-3 mt-2 rounded space-y-2 bg-gray-50">
                    <div>
                      <label>Select Existing Symptom (L2/L3)</label>
                      <Select
                        value={
                          symptom.existingSymptomId
                            ? {
                              value: symptom.existingSymptomId,
                              label:
                                allL2L3Symptoms.find(s => s.sid === parseInt(symptom.existingSymptomId))?.name ||
                                'Select',
                            }
                            : null
                        }
                        onChange={(option) =>
                          handleExistingSymptomSelect(index, option ? option.value : '')
                        }
                        options={allL2L3Symptoms.map((s) => ({
                          value: s.sid,
                          label: `${s.name} (${s.level === 2 ? 'L2' : 'L3'})`,
                        }))}
                        placeholder="Select"
                        isClearable
                      />
                    </div>
                    {/* Normal Input Fields */}
                    <div>
                      <label>{t('expert.editProblem.symptomNameLabel')}</label>
                      <input
                        name="name"
                        value={symptom.name}
                        onChange={(e) => handleNewSymptomChange(index, e)}
                        className="w-full border p-2 rounded"
                        disabled={!!symptom.existingSymptomId}
                      />
                    </div>
                    <div>
                      <label>{t('expert.editProblem.questionLabel')}</label>
                      <input
                        name="qn"
                        value={symptom.qn}
                        onChange={(e) => handleNewSymptomChange(index, e)}
                        className="w-full border p-2 rounded"
                        disabled={!!symptom.existingSymptomId}
                      />
                    </div>

                    <div>
                      <label>Level</label>
                      <select
                        name="level"
                        value={symptom.level}
                        onChange={(e) => handleNewSymptomChange(index, e)}
                        className="w-full border p-2 rounded"
                        disabled={!!symptom.existingSymptomId}
                      >
                        <option value={2}>L2</option>
                        <option value={3}>L3</option>
                      </select>
                    </div>

                    {/* <div>
                      <label> L1 Symptom</label>
                      <select
                        name="parentId"
                        value={symptom.parentId || ''}
                        onChange={(e) => handleNewSymptomChange(index, e)}
                        className="w-full border p-2 rounded"
                        disabled={!!symptom.existingSymptomId}
                      >
                        <option value="">None</option>
                        {l1Symptoms.map(l1 => (
                          <option key={l1.sid} value={l1.sid}>
                            {l1.name}
                          </option>
                        ))}
                      </select>
                    </div> */}
                    {/* Parent selection depends on level */}
                    {symptom.level === 2 ? (
                      <div>
                        <label>L1 Symptom</label>
                        <select
                          name="parentId"
                          value={symptom.parentId || ''}
                          onChange={(e) => handleNewSymptomChange(index, e)}
                          className="w-full border p-2 rounded"
                          disabled={!!symptom.existingSymptomId}
                        >
                          <option value="">Select L1 Symptom</option>
                          {l1Symptoms.map((l1) => (
                            <option key={l1.sid} value={l1.sid}>
                              {l1.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label>L2 Symptom</label>
                        <select
                          name="parentId"
                          value={symptom.parentId || ''}
                          onChange={(e) => handleNewSymptomChange(index, e)}
                          className="w-full border p-2 rounded"
                          disabled={!!symptom.existingSymptomId}
                        >
                          <option value="">Select L2 Symptom</option>
                          {l2Symptoms.map((l2) => (
                            <option key={l2.sid} value={l2.sid}>
                              {l2.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => removeNewSymptom(index)}
                      className="text-red-500 mt-2"
                    >
                      {t('expert.editProblem.deleteSymptomButton')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setNewSymptoms(prev => [
                  ...prev,
                  {
                    existingSymptomId: '', // NEW FIELD
                    sid: undefined,
                    name: '',
                    qn: '',
                    level: 2,
                    parentId: null,
                    grandparentId: null,
                    description: '',
                  }
                ]);
              }}
              className="mb-6 p-2 text-blue-500"
            >
              + {t('expert.editProblem.addSymptomButton')}
            </button>

            <button
              onClick={handleSubmit}
              className="mt-6 w-full bg-blue-500 text-white p-3 rounded"
            >
              {t('expert.editProblem.submitButton')}
            </button>
          </>
        )}
      </div>
      {/* LEFT: Crop and Problems */}
      <div className="w-1/3 bg-gray-100 p-4 rounded shadow">
        <h2 className="font-bold text-lg mb-3">{t('expert.editProblem.existingCropsTitle')}</h2>
        {crops.map(crop => (
          <div
            key={crop.cid}
            onClick={async () => {
              setSelectedCrop(crop);
              const cropDiseases = await fetchDiseases(crop.cid, true);
              setDiseases(cropDiseases);
              if (cropDiseases.length > 0) {
                selectDisease(cropDiseases[0]);
              }
            }}
            className={`p-2 cursor-pointer rounded ${selectedCrop?.cid === crop.cid ? 'bg-blue-200' : ''}`}
          >
            {crop.name}
          </div>
        ))}

        <h2 className="font-bold text-lg mt-6 mb-2">{t('expert.editProblem.existingProblemsTitle')}</h2>
        {diseases.map(disease => (
          <div
            key={disease.disId}
            onClick={() => selectDisease(disease)}
            className={`p-2 cursor-pointer rounded text-lg ${selectedDisease?.disId === disease.disId ? 'bg-blue-200' : ''}`}
          >
            {disease.chemical_name || disease.scientific_name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditProblem;